import { useState } from "react";
import { CopyOutlined, EditOutlined } from "@ant-design/icons";
import { Alert, Button, message, Select, Space, Typography } from "antd";

import { LandingPageStatus } from "@fitness-app/data-models/entities/LandingPage";
import {
  FUNNEL_PAGE_TEMPLATES,
  hasOwnFunnelRoutes,
  getFunnelStepPageTemplate,
  type FunnelPageTemplate,
  type SalesFunnelStep,
} from "@fitness-app/data-models/entities/SalesFunnel";

import { FUNNEL_TEMPLATE_SLUG_PREFIX } from "~/modules/Landings/landingSlug";
import { useLandings } from "~/modules/Landings/useLandings";

const CODE_PREFIX = "code:";
const PAGE_PREFIX = "page:";

interface Props {
  step: Pick<SalesFunnelStep, "type" | "stepConfig">;
  /** Funnels with their own routes in code ignore the step's page. */
  funnelSlug: string;
  onChange: (patch: { pageTemplate?: FunnelPageTemplate; landingPageId: string | null }) => void;
}

/**
 * "Szablon strony" of a funnel step: a page coded in ecommerce, or a page from the landing
 * builder of the step's kind (TRE-2356), with shortcuts to edit it or to make an own copy.
 */
export const FunnelStepPageSelect = ({ step, funnelSlug, onChange }: Props) => {
  const { data: pages = [], duplicate } = useLandings();
  const [copying, setCopying] = useState(false);
  const pageId = step.stepConfig.landingPageId ?? null;
  // Marketing landings (with their own signup flow) stay out: only funnel pages under `szablony/`.
  const builderPages = pages.filter(
    (page) => String(page.kind ?? "landing") === String(step.type) && page.slug.startsWith(FUNNEL_TEMPLATE_SLUG_PREFIX),
  );
  const selectedPage = pageId ? pages.find((page) => page.id === pageId) : undefined;
  // The step renders only a page of its own kind; another kind falls back to the code template.
  const wrongKind = Boolean(selectedPage) && String(selectedPage!.kind ?? "landing") !== String(step.type);

  const value = pageId ? `${PAGE_PREFIX}${pageId}` : `${CODE_PREFIX}${getFunnelStepPageTemplate(step)}`;

  const copyAndUse = async () => {
    if (!selectedPage) return;
    setCopying(true);
    try {
      const copyId = await duplicate(selectedPage, { publish: true });
      onChange({ landingPageId: copyId });
      message.success("Kopia utworzona i podpięta pod ten krok. Zapisz funnel, a potem edytuj kopię.");
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Nie udało się skopiować strony");
    } finally {
      setCopying(false);
    }
  };

  if (hasOwnFunnelRoutes(funnelSlug)) {
    return (
      <Alert
        type="info"
        showIcon
        message="Ten funnel ma własne strony w kodzie (/oferta/…), więc szablon kroku nie ma tu znaczenia."
        description="Żeby użyć strony z buildera, zrób kopię funnelu pod nowym slugiem."
      />
    );
  }

  return (
    <Space direction="vertical" className="w-full">
      <Select
        value={value}
        showSearch
        optionFilterProp="label"
        options={[
          {
            label: "Szablony z kodu",
            options: FUNNEL_PAGE_TEMPLATES[step.type].map((template) => ({
              label: template.label,
              value: `${CODE_PREFIX}${template.value}`,
            })),
          },
          {
            label: "Strony z buildera",
            options: builderPages.map((page) => ({
              label: page.status === LandingPageStatus.Published ? page.name : `${page.name} (szkic)`,
              value: `${PAGE_PREFIX}${page.id}`,
            })),
          },
        ]}
        onChange={(next: string) =>
          next.startsWith(PAGE_PREFIX)
            ? onChange({ landingPageId: next.slice(PAGE_PREFIX.length) })
            : onChange({ pageTemplate: next.slice(CODE_PREFIX.length) as FunnelPageTemplate, landingPageId: null })
        }
      />
      {pageId ? (
        <>
          {!selectedPage ? (
            <Alert type="warning" showIcon message="Tej strony nie ma już w builderze. Krok pokaże szablon z kodu." />
          ) : wrongKind ? (
            <Alert
              type="warning"
              showIcon
              message="Ta strona jest innego rodzaju niż krok. Krok pokazuje szablon z kodu; wybierz stronę pasującą do kroku."
            />
          ) : selectedPage.status !== LandingPageStatus.Published ? (
            <Alert
              type="warning"
              showIcon
              message="Strona nie jest opublikowana. Do czasu publikacji krok pokazuje szablon z kodu."
            />
          ) : null}
          <Space wrap>
            <Button icon={<EditOutlined />} href={`/landings?edit=${pageId}`} target="_blank" disabled={!selectedPage}>
              Edytuj stronę
            </Button>
            <Button
              icon={<CopyOutlined />}
              loading={copying}
              disabled={!selectedPage || wrongKind}
              onClick={copyAndUse}
            >
              Zrób kopię dla tego funnelu
            </Button>
          </Space>
          <Typography.Text type="secondary" className="text-xs">
            Zmiany w stronie widać we wszystkich funnelach, które jej używają. Żeby zmienić ją tylko tutaj, zrób kopię.
          </Typography.Text>
        </>
      ) : (
        <Typography.Text type="secondary" className="text-xs">
          Szablony z oferty dodasz do buildera w Landingi → Szablony z oferty.
        </Typography.Text>
      )}
    </Space>
  );
};
